import type { ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * A raised plane — the one container this product has.
 *
 * It separates from the page by light, never by a border, and there is exactly
 * one level of it: a plane never sits inside another plane. Where content needs
 * dividing, a hairline rule or space does the work, because a card drawn inside
 * a card reads as two objects when it is one.
 *
 * The header is optional and deliberately plain: a label, not a title bar. The
 * figures inside are what the eye should land on.
 */

export interface PlaneProps {
  /** Small uppercase caption naming what the plane holds. */
  label?: ReactNode;
  /** One line of context under the label. */
  note?: ReactNode;
  /** Right-aligned action — a link out, a control. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Removes the inner padding, for planes that hold a full-bleed table or chart. */
  flush?: boolean;
  /**
   * The single most important plane on a screen may sit one step higher. Use
   * once per screen or not at all.
   */
  raised?: boolean;
  as?: 'section' | 'div' | 'article';
}

export function Plane({
  label,
  note,
  action,
  children,
  className,
  flush = false,
  raised = false,
  as: Tag = 'section',
}: PlaneProps) {
  const hasHeader = Boolean(label || action || note);
  return (
    <Tag className={clsx(raised ? 'plane-raised' : 'plane', 'flex flex-col', className)}>
      {hasHeader ? (
        <header
          className={clsx(
            'flex items-start justify-between gap-3',
            flush ? 'px-4 pt-4 pb-3 sm:px-5' : 'px-4 pt-4 sm:px-5 sm:pt-5',
          )}
        >
          <div className="min-w-0">
            {label ? <h2 className="label">{label}</h2> : null}
            {note ? <p className="mt-1 text-micro text-muted">{note}</p> : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </header>
      ) : null}
      {/* `min-h-0`: in a plane given a fixed height, the body must not grow past
          it with its content — the map plane fills what is left, and a body
          allowed to grow overflowed the plane instead (5 Sep 2026). */}
      <div
        className={clsx(
          'min-h-0 min-w-0 flex-1',
          flush ? '' : 'p-4 sm:p-5',
          hasHeader && !flush ? 'pt-3 sm:pt-4' : '',
        )}
      >
        {children}
      </div>
    </Tag>
  );
}

/**
 * A hairline that divides content inside a plane. The only line in the system:
 * it marks a genuine division, never decorates an edge.
 */
export function Rule({ className }: { className?: string }) {
  return <hr className={clsx('border-0 border-t border-line', className)} />;
}
